"use client";

/**
 * Context provider for player page data.
 *
 * Provides normalized player data to all child components, allowing them to
 * access player profile, matches, posts, and helper functions without
 * prop drilling.
 */

import React, { createContext, useContext, useMemo } from "react";
import type { PlayerPageData } from "@/utilities/data/player-page-types";
import type MatchFullModel from "fansunited-sdk-esm/Core/Namespaces/Football/Models/Match/MatchFullModel";
import type { Post } from "@/payload-types";

/**
 * Extended context value with computed helpers.
 */
export interface PlayerPageDataContextValue extends PlayerPageData {
    /** Get the player's next match (from SDK football namespace) */
    getNextMatch: () => MatchFullModel | null | undefined;
    /** Get the player's previous match (from SDK football namespace) */
    getPreviousMatch: () => MatchFullModel | null | undefined;
    /** Get initial articles for the player (for News tab) */
    getArticles: () => Post[];
    /** Get total count of articles available */
    getTotalArticles: () => number;
    /** Get total number of pages */
    getTotalPages: () => number;
    /** Get articles per page (for consistent pagination) */
    getArticlesPerPage: () => number;
    /** Get initial videos for the player (for Videos tab) */
    getVideos: () => Post[];
    /** Get total count of videos available */
    getTotalVideos: () => number;
    /** Get total number of video pages */
    getVideosTotalPages: () => number;
    /** Get videos per page */
    getVideosPerPage: () => number;
    /** Check if any videos exist (to show/hide Videos tab) */
    hasAnyVideos: () => boolean;
}

const PlayerPageDataContext = createContext<PlayerPageDataContextValue | null>(null);

interface PlayerPageDataProviderProps {
    data: PlayerPageData;
    children: React.ReactNode;
}

/**
 * Provider component that makes player page data available to all child components.
 *
 * Usage in a server component:
 * ```tsx
 * const data = await getPlayerPageData({ playerId, locale }, tenant);
 *
 * return (
 *     <PlayerPageDataProvider data={data}>
 *         <PlayerPageLayout />
 *     </PlayerPageDataProvider>
 * );
 * ```
 */
export function PlayerPageDataProvider({ data, children }: PlayerPageDataProviderProps) {
    const contextValue = useMemo<PlayerPageDataContextValue>(() => {
        return {
            ...data,
            getNextMatch: () => data.playerNextMatch,
            getPreviousMatch: () => data.playerPrevMatch,
            // Posts helpers
            getArticles: () => data.posts?.articles ?? [],
            getTotalArticles: () => data.posts?.totalArticles ?? 0,
            getTotalPages: () => data.posts?.totalPages ?? 0,
            getArticlesPerPage: () => data.posts?.articlesPerPage ?? 12,
            // Video helpers
            getVideos: () => data.posts?.videos ?? [],
            getTotalVideos: () => data.posts?.totalVideos ?? 0,
            getVideosTotalPages: () => data.posts?.videosTotalPages ?? 0,
            getVideosPerPage: () => data.posts?.videosPerPage ?? 12,
            hasAnyVideos: () => (data.posts?.totalVideos ?? 0) > 0,
        };
    }, [data]);

    return <PlayerPageDataContext.Provider value={contextValue}>{children}</PlayerPageDataContext.Provider>;
}

/**
 * Hook to access player page data in client components.
 * Must be used within a PlayerPageDataProvider.
 *
 * @throws Error if used outside of PlayerPageDataProvider
 *
 * Usage:
 * ```tsx
 * "use client";
 * import { usePlayerPageData } from "@/views/player/context";
 *
 * function PlayerStats() {
 *     const { player, matches, getNextMatch } = usePlayerPageData();
 *     // ...
 * }
 * ```
 */
export function usePlayerPageData(): PlayerPageDataContextValue {
    const context = useContext(PlayerPageDataContext);

    if (!context) {
        throw new Error("usePlayerPageData must be used within a PlayerPageDataProvider");
    }

    return context;
}

/**
 * Hook to optionally access player page data.
 * Returns null if not within a PlayerPageDataProvider (useful for shared components).
 */
export function usePlayerPageDataOptional(): PlayerPageDataContextValue | null {
    return useContext(PlayerPageDataContext);
}
